import { Component, lazy, Suspense, useEffect, type ReactNode } from "react";
import { HashRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { isPasswordChangePath } from "./auth/mfa";
import { FinishSetup, ForgotPassword, LogIn, MfaChallenge, SignUp } from "./auth/AuthScreens";
import { hasReturn, keepReturn } from "./panel/quickbooksConnect";
import { BrandLoading } from "./runner/Mark";
import { Wordmark } from "./runner/Wordmark";
import { AsyncButton, Banner, ToastProvider } from "./ui/kit";
import { isChunkError, loadChunk, reloadOnce } from "./lib/staleChunk";

// The signed-in desk (panel, tools, PDF and forms code) is its own part of the build, loaded once a session is in:
// the sign-up, log-in and code screens never wait for it. A part gone after a deploy reloads the tab once.
const loadDesk = () => loadChunk(() => import("./panel/Desk"));
const Desk = lazy(loadDesk);

// Keept Runner: the accountant's desk over QuickBooks Online, and nothing else. Hash routes: the same
// build serves app.usekeept.com (where /panel redirects to /#/panel) and Electron's file:// window.
export function App() {
  return (
    <Crash>
      <ToastProvider>
        <AuthProvider>
          <HashRouter>
            <Gate />
          </HashRouter>
        </AuthProvider>
      </ToastProvider>
    </Crash>
  );
}

/**
 * Routes and their guards. Signed out: #/login, #/signup, #/forgot-password; anything else goes to #/login.
 * Signed in: #/panel; #/login and #/signup go to the panel. #/forgot-password stays on screen while a reset is under
 * way (the emailed code signs the tab in before the new password is saved) and lets go once it's saved.
 * #/quickbooks?ref=… | ?error=… is where Intuit's sign-in comes back (through the server's 302): the answer is kept for
 * this tab and the address cleared at once; signed in, Settings → QuickBooks finishes it; signed out, after logging in.
 * Two-step verification: a session that has the password but not yet the authenticator code (aal1, the account has a
 * verified factor) sees only the code step, whatever the address. The one exception is a password change under way
 * (#/forgot-password, or Account → Change password): its emailed code made an aal1 session, and once the new
 * password is saved the code step follows.
 */
function Gate() {
  const { ready, session, resetting, mfaNeeded } = useAuth();
  const { pathname } = useLocation();
  if (!ready) return <BrandLoading label="Starting Keept Runner" />;
  const signedIn = !!session && !session.user.is_anonymous;
  if (signedIn && mfaNeeded && !(resetting && isPasswordChangePath(pathname))) {
    return (
      <Routes>
        <Route path="/quickbooks" element={<QuickBooksReturn signedIn={false} />} />
        <Route path="*" element={<MfaChallenge />} />
      </Routes>
    );
  }
  const home = hasReturn() ? "/panel/qbo?tab=connections" : "/panel";
  const publicOnly = (screen: ReactNode) => (signedIn ? <Navigate to={home} replace /> : screen);
  return (
    <Routes>
      <Route path="/quickbooks" element={<QuickBooksReturn signedIn={signedIn} />} />
      <Route path="/login" element={publicOnly(<LogIn />)} />
      <Route path="/signup" element={publicOnly(<SignUp />)} />
      <Route path="/forgot-password" element={signedIn && !resetting ? <Navigate to="/panel" replace /> : <ForgotPassword />} />
      <Route path="/panel/*" element={signedIn ? <Account /> : <Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to={signedIn ? "/panel" : "/login"} replace />} />
    </Routes>
  );
}

/** Intuit's sign-in came back: keep the answer for this tab, then leave this address (the one-time id goes with it). */
function QuickBooksReturn({ signedIn }: { signedIn: boolean }) {
  const [params] = useSearchParams();
  keepReturn(params);
  return <Navigate to={signedIn ? "/panel/qbo?tab=connections" : "/login"} replace />;
}

/** Signed in: the account's own screens first (setup, a problem), then the panel. */
function Account() {
  const { profile, needsSetup, profileError } = useAuth();
  // Fetch the desk while the profile is still loading (or setup is on screen), not after.
  useEffect(() => { loadDesk().catch(() => {}); }, []);
  if (needsSetup) return <FinishSetup />;
  if (!profile) return profileError ? <ProfileProblem error={profileError} /> : <BrandLoading label="Loading your account" />;
  if (profile.role !== "accountant") return <NotAnOffice />;
  return <Suspense fallback={<DeskLoading />}><Desk /></Suspense>;
}

/** While the desk's part of the build loads: the three squares turning on the same ground as the other loading screens. */
function DeskLoading() {
  return <BrandLoading />;
}

/** An account that isn't an accountant's (the retired client app's): one plain screen, a way out, and the one address. */
function NotAnOffice() {
  const { session, signOut } = useAuth();
  return (
    <div className="kr-solo">
      <div className="kr-solo-brand"><Wordmark mark size={22} /></div>
      <div className="kr-light kr-solo-card">
        <div className="kr-wizard">
          <div className="kr-h">This sign-in is for accountants</div>
          <p className="kr-sub">Keept Runner is the desk for accounting firms on QuickBooks Online, and <b>{session?.user.email}</b> isn't an accountant's account. To work here, create a Keept Runner account with your work email.</p>
          <p className="kr-sub">Questions: <a className="kr-link" href="mailto:kept@usekeept.com">kept@usekeept.com</a></p>
          <div className="kr-row">
            <button type="button" className="kr-btn ghost" onClick={() => void signOut()}>Log out</button>
            <span className="grow" />
            <a className="kr-btn primary" href="mailto:kept@usekeept.com">Write to Keept</a>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The account's profile couldn't be read (offline, server down): a retry, never an endless spinner. */
function ProfileProblem({ error }: { error: string }) {
  const { refreshProfile, signOut } = useAuth();
  return (
    <div className="kr-solo">
      <div className="kr-solo-brand"><Wordmark mark size={22} /></div>
      <div className="kr-light kr-solo-card">
        <div className="kr-wizard">
          <div className="kr-h">Couldn't load your account</div>
          <p className="kr-sub">You're signed in, but Keept couldn't read your account details. Nothing was lost; check your connection and try again.</p>
          <Banner tone="err">{error}</Banner>
          <div className="kr-row">
            <button type="button" className="kr-btn ghost" onClick={() => void signOut()}>Log out</button>
            <span className="grow" />
            <AsyncButton className="kr-btn primary" onRun={refreshProfile}>Try again</AsyncButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Last line of defence: a render error shows what happened and a way back, never a blank page. */
class Crash extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    // A part of the old build gone after a deploy: one reload brings the new one.
    if (isChunkError(error) && reloadOnce()) return;
    console.error("Keept Runner crashed", error);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="kr-solo">
        <div className="kr-light kr-solo-card">
          <div className="kr-wizard">
            <div className="kr-h">Something went wrong</div>
            <p className="kr-sub">Keept Runner hit an unexpected error. Everything approved is on the server; nothing in QuickBooks changed because of this.</p>
            <pre className="kr-faint" style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{this.state.error.message}</pre>
            <div className="kr-row"><span className="grow" /><button type="button" className="kr-btn primary" onClick={() => location.reload()}>Reload</button></div>
          </div>
        </div>
      </div>
    );
  }
}
